import { useContext, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CalendarPlus,
  CloudDownloadIcon,
  FileTextIcon,
  FolderMinusIcon,
  LocationIcon,
  PencilIcon,
  QrIcon,
  SendIcon,
  ThreeDots,
  TrashIcon,
} from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { NotificationType } from 'common/enums';
import { DropDownListItem } from 'common/drop-down-list-item';
import { Menu } from 'common/menu';
import { ModalWarning } from 'common/modal-warning';
import { PrivateToggleItem } from 'common/private-toggle-item';
import { QrCodeCard } from 'common/qr-code-card';
import { ShareDialog } from 'common/share-dialog';
import { DateMenu } from 'common/date-picker/components/date-menu';
import { LocationMenu } from 'common/album-detail-modal/components/menus/location-menu';
import { CenterModal } from 'pages/album-view-mode/components/center-modal';
import { appActions, AppContext } from 'context/app-context';
import { downloadMemory } from 'helpers/download-memory';
import { memoryQrCodes } from 'helpers/memories';
import { classnames } from 'helpers/utils';
import { vaultAccess } from 'helpers/sharing';
import { useClickOutside } from 'hooks/click-outside';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import styles from './memory-card-menu.module.scss';

type ActiveModal =
  | 'rename'
  | 'description'
  | 'date'
  | 'location'
  | 'qr'
  | 'remove'
  | 'delete'
  | 'share'
  | null;

interface MemoryCardMenuProps {
  content: AlbumContentSerialized;
  onContentUpdated: (
    contentId: number,
    partial: Partial<AlbumContentSerialized>,
  ) => void;
  // The memory left this album: deleted, or moved to Unorganized Memories.
  onContentRemoved: (contentId: number) => void;
  // The vault's hidden default album, where "Remove from album" files the
  // memory. Absent on that album itself: there's nothing to remove it from.
  unsortedAlbumId?: number;
}

// The per-memory "•••" menu shown on each album-grid card. Offers what the
// memory's view page does, with the same modals/menus: rename, description,
// date, location, download, QR code, privacy, remove from album and delete.
export const MemoryCardMenu = ({
  content,
  onContentUpdated,
  onContentRemoved,
  unsortedAlbumId,
}: MemoryCardMenuProps) => {
  const { dispatch, state } = useContext(AppContext);
  // Unorganized Memories is part of the vault, not the album: someone the
  // album alone was shared with can't see it there afterwards.
  const inVault = vaultAccess(state.data.user, state.currentVault);
  const seesUnsorted = inVault.fullAccess || inVault.role === 'contributor';
  const { id: vaultId } = useParams<{ id?: string }>();
  const [showMenu, setShowMenu] = useState(false);
  const [modal, setModal] = useState<ActiveModal>(null);
  const [nameDraft, setNameDraft] = useState(content.name ?? '');
  const [descriptionDraft, setDescriptionDraft] = useState(
    content.description ?? '',
  );
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapRef, () => setShowMenu(false));
  // Printed QR codes that open this memory.
  const qrCodes = memoryQrCodes(content);
  // What this person's role allows with this memory (the API says; the
  // owner's and editors' memories carry all of it).
  const can = content.myAccess ?? {
    canEdit: true,
    canDescribe: true,
    canSetPrivacy: true,
    canShare: true,
  };

  const notify = (title: string, type: NotificationType) =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({ show: true, title, type }),
    });

  const openModal = (next: Exclude<ActiveModal, null>) => {
    setShowMenu(false);
    if (next === 'rename') setNameDraft(content.name ?? '');
    if (next === 'description') setDescriptionDraft(content.description ?? '');
    setModal(next);
  };
  const close = () => setModal(null);

  const handleRename = async () => {
    const name = nameDraft.trim();
    close();
    if (!name || name === (content.name ?? '')) return;
    try {
      await VaultsController.updateContentMetadata(content.id, { name });
      onContentUpdated(content.id, { name });
      notify('Memory renamed', NotificationType.Success);
    } catch (error) {
      notify('The memory could not be renamed', NotificationType.Error);
    }
  };

  const handleSaveDescription = async () => {
    const description = descriptionDraft.trim();
    close();
    if (description === (content.description ?? '')) return;
    try {
      await VaultsController.updateContentMetadata(content.id, {
        description,
      });
      onContentUpdated(content.id, { description });
      notify('Description updated', NotificationType.Success);
    } catch (error) {
      notify('The description could not be updated', NotificationType.Error);
    }
  };

  const handleDownload = async () => {
    setShowMenu(false);
    try {
      await downloadMemory(content);
    } catch (error) {
      notify('The memory could not be downloaded', NotificationType.Error);
    }
  };

  // Stays open after toggling, so the switch shows the new state.
  const handleTogglePrivate = async () => {
    const isPrivate = !content.isPrivate;
    try {
      setSavingPrivacy(true);
      await VaultsController.updateContentMetadata(content.id, { isPrivate });
      onContentUpdated(content.id, { isPrivate });
      notify(
        isPrivate ? 'Memory is now private' : 'Memory is no longer private',
        NotificationType.Success,
      );
    } catch (error) {
      notify('The memory could not be updated', NotificationType.Error);
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleUpdateDate = async (startDate: string, endDate: string) => {
    close();
    try {
      await VaultsController.updateContentMetadata(content.id, {
        startDate,
        endDate,
      });
      onContentUpdated(content.id, { startDate, endDate });
      notify('Date updated', NotificationType.Success);
    } catch (error) {
      notify('The date could not be updated', NotificationType.Error);
    }
  };

  // The memory's own location; its album's is set on the album.
  const handleUpdateLocation = async (location: string) => {
    close();
    try {
      await VaultsController.updateContentMetadata(content.id, { location });
      onContentUpdated(content.id, { location });
      notify('Location updated', NotificationType.Success);
    } catch (error) {
      notify('The location could not be updated', NotificationType.Error);
    }
  };

  // Files the memory in the vault's hidden default album, so it shows under
  // Unorganized Memories on the dashboard. The memory itself is kept as is.
  const handleRemoveFromAlbum = async () => {
    close();
    if (unsortedAlbumId == null) return;
    try {
      await VaultsController.updateContentMetadata(content.id, {
        albumId: unsortedAlbumId,
      });
      onContentRemoved(content.id);
      notify('Memory moved to Unorganized Memories', NotificationType.Success);
    } catch (error) {
      notify(
        'The memory could not be removed from the album',
        NotificationType.Error,
      );
    }
  };

  const handleDelete = async () => {
    close();
    try {
      await VaultsController.deleteAlbumContent(
        content.id,
        content.fileSize ?? 0,
      );
      onContentRemoved(content.id);
      notify('Memory deleted', NotificationType.Success);
    } catch (error) {
      notify('The memory could not be deleted', NotificationType.Error);
    }
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={classnames(styles.trigger, 'gs-card-menu-trigger', {
          [styles.triggerOpen]: showMenu,
        })}
        aria-label="Memory options"
        onClick={() => setShowMenu((prev) => !prev)}
      >
        <ThreeDots />
      </button>

      {showMenu && (
        <Menu position="" className={styles.menu}>
          {can.canEdit && (
            <DropDownListItem
              leftIcon={<PencilIcon />}
              onClick={() => openModal('rename')}
            >
              Rename
            </DropDownListItem>
          )}
          {can.canDescribe && (
            <DropDownListItem
              leftIcon={<FileTextIcon />}
              onClick={() => openModal('description')}
            >
              {content.description ? 'Edit description' : 'Add description'}
            </DropDownListItem>
          )}
          {can.canEdit && (
            <DropDownListItem
              leftIcon={<CalendarPlus />}
              onClick={() => openModal('date')}
            >
              Change date
            </DropDownListItem>
          )}
          {can.canEdit && (
            <DropDownListItem
              leftIcon={<LocationIcon />}
              onClick={() => openModal('location')}
            >
              Change location
            </DropDownListItem>
          )}
          {can.canShare && vaultId && (
            <DropDownListItem
              leftIcon={<SendIcon />}
              onClick={() => openModal('share')}
            >
              Share
            </DropDownListItem>
          )}
          <DropDownListItem
            leftIcon={<CloudDownloadIcon />}
            onClick={handleDownload}
          >
            Download
          </DropDownListItem>
          {can.canEdit && qrCodes.length > 0 && (
            <DropDownListItem
              leftIcon={<QrIcon />}
              onClick={() => openModal('qr')}
            >
              Show QR code
            </DropDownListItem>
          )}
          {can.canSetPrivacy && (
            <PrivateToggleItem
              isPrivate={content.isPrivate}
              onToggle={handleTogglePrivate}
              disabled={savingPrivacy}
            />
          )}
          {can.canEdit && unsortedAlbumId != null && (
            <DropDownListItem
              leftIcon={<FolderMinusIcon />}
              onClick={() => openModal('remove')}
            >
              Remove from album
            </DropDownListItem>
          )}
          {can.canEdit && (
            <DropDownListItem
              leftIcon={<TrashIcon />}
              onClick={() => openModal('delete')}
            >
              Delete
            </DropDownListItem>
          )}
        </Menu>
      )}

      {modal === 'share' && vaultId && (
        <ShareDialog
          target={{
            type: 'memory',
            vaultId: Number(vaultId),
            id: content.id,
            name: content.name || 'This memory',
            isPrivate: content.isPrivate,
          }}
          onMakeVisible={
            can.canSetPrivacy
              ? async () => {
                  await VaultsController.updateContentMetadata(content.id, {
                    isPrivate: false,
                  });
                  onContentUpdated(content.id, { isPrivate: false });
                }
              : undefined
          }
          onClose={close}
        />
      )}

      {modal === 'rename' && (
        <CenterModal onClose={close}>
          <div className={styles.renameBody}>
            <span className={styles.renameTitle}>Rename memory</span>
            <input
              type="text"
              className={styles.renameInput}
              value={nameDraft}
              maxLength={120}
              placeholder="Memory name"
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename();
              }}
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
            />
            <div className={styles.renameActions}>
              <Button
                buttonStyle={ButtonStyle.SecondaryGray}
                buttonSize={ButtonSize.Medium}
                onClick={close}
              >
                Cancel
              </Button>
              <Button
                buttonStyle={ButtonStyle.Primary}
                buttonSize={ButtonSize.Medium}
                disabled={!nameDraft.trim()}
                onClick={handleRename}
              >
                Save
              </Button>
            </div>
          </div>
        </CenterModal>
      )}

      {modal === 'description' && (
        <CenterModal onClose={close}>
          <div className={styles.renameBody}>
            <span className={styles.renameTitle}>Description</span>
            <textarea
              className={classnames(
                styles.renameInput,
                styles.descriptionInput,
              )}
              value={descriptionDraft}
              placeholder="Add a description"
              onChange={(e) => setDescriptionDraft(e.target.value)}
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
            />
            <div className={styles.renameActions}>
              <Button
                buttonStyle={ButtonStyle.SecondaryGray}
                buttonSize={ButtonSize.Medium}
                onClick={close}
              >
                Cancel
              </Button>
              <Button
                buttonStyle={ButtonStyle.Primary}
                buttonSize={ButtonSize.Medium}
                onClick={handleSaveDescription}
              >
                Save
              </Button>
            </div>
          </div>
        </CenterModal>
      )}

      {modal === 'qr' && (
        <CenterModal onClose={close}>
          <div className={styles.renameBody}>
            <span className={styles.renameTitle}>QR code</span>
            {qrCodes.map((qrCode) => (
              <QrCodeCard key={qrCode.id} qrCode={qrCode} />
            ))}
          </div>
        </CenterModal>
      )}

      {modal === 'date' && (
        <CenterModal onClose={close}>
          <DateMenu
            renderInline
            subject="memory"
            currentDate={content.startDate}
            currentFinalDate={content.endDate}
            onUpdateDate={handleUpdateDate}
          />
        </CenterModal>
      )}

      {modal === 'location' && (
        <CenterModal onClose={close}>
          <div className={styles.locationBody}>
            <LocationMenu renderInline addLocation={handleUpdateLocation} />
          </div>
        </CenterModal>
      )}

      {modal === 'remove' && (
        <ModalWarning
          title="Remove from album"
          content={
            seesUnsorted
              ? "Are you sure you want to remove this memory from the album? It won't be deleted: you'll find it under Unorganized Memories on your dashboard."
              : "Remove this memory from the album? It won't be deleted: it moves to the vault's Unorganized Memories, which only the vault's owner and members see. You won't see it anymore."
          }
          successButtonText="Remove"
          successStyleButton={ButtonStyle.Primary}
          closeFn={close}
          successFn={handleRemoveFromAlbum}
        />
      )}

      {modal === 'delete' && (
        <ModalWarning
          title="Delete memory"
          content="Are you sure you want to delete this Entry? This action cannot be undone."
          successButtonText="Delete"
          successStyleButton={ButtonStyle.DestructivePrimary}
          closeFn={close}
          successFn={handleDelete}
        />
      )}
    </div>
  );
};
